import { Component, ChangeDetectionStrategy, inject } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { PopupBenefitHorizontalComponent } from './shared/popup-benefit-horizontal.component';
import { BreakpointObserver } from '@angular/cdk/layout';
import { PopupBenefitDividerComponent } from './shared/popup-benefit-divider.component';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { map } from 'rxjs';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PopupBenefitHorizontalComponent, PopupBenefitDividerComponent, NgTemplateOutlet, AsyncPipe],
  template: `
    <div class="popup popup--dark">
      <div class="popup__background">
        <div class="popup__heading popup__heading--shadow color-autopay">Streamline Your Loan Experience with</div>
        <div class="autopay-responsive-subtitle">
          <div>
            <a class="btn-autopay" href="/loanmanager/eftsetuprequest/">Auto Pay</a>
          </div>
          <div>Effortlessly Manage Your Monthly Payments</div>
        </div>

        <div class="benefits-container">
          <div class="popup__subheading color-autopay">Benefits of Auto Payments</div>

          @if (isNarrow$ | async; as isNarrow) {
            <div class="benefits-container__items">
              <app-popup-benefit-divider direction="horizontal" cssClass="color-autopay" />
              <ng-container *ngTemplateOutlet="convenience"></ng-container>
              <app-popup-benefit-divider direction="horizontal" cssClass="color-autopay" />
              <ng-container *ngTemplateOutlet="costFree"></ng-container>
              <app-popup-benefit-divider direction="horizontal" cssClass="color-autopay" />
              <ng-container *ngTemplateOutlet="hassleFree"></ng-container>
              <app-popup-benefit-divider direction="horizontal" cssClass="color-autopay" />
              <ng-container *ngTemplateOutlet="avoidDelays"></ng-container>
              <app-popup-benefit-divider direction="horizontal" cssClass="color-autopay" />
              <ng-container *ngTemplateOutlet="flexibility"></ng-container>
            </div>
          } @else {
            <div class="benefits-container__horizontal-subdivider">
              <div class="benefits-container__items">
                <ng-container *ngTemplateOutlet="convenience"></ng-container>
                <app-popup-benefit-divider direction="horizontal" cssClass="color-autopay" />
                <ng-container *ngTemplateOutlet="costFree"></ng-container>
                <app-popup-benefit-divider direction="horizontal" cssClass="color-autopay" />
                <ng-container *ngTemplateOutlet="flexibility"></ng-container>
              </div>
              <div class="benefits-container__items">
                <ng-container *ngTemplateOutlet="hassleFree"></ng-container>
                <app-popup-benefit-divider direction="horizontal" cssClass="color-autopay" />
                <ng-container *ngTemplateOutlet="avoidDelays"></ng-container>
              </div>
            </div>
          }
        </div>
      </div>
      <div class="popup__bottom-container">
        <p>
          <a href (click)="close($event)">Not at this time, I prefer to pay manually</a>
        </p>
      </div>
    </div>

    <ng-template #convenience>
      <app-popup-benefit-horizontal
        src="/media/static/app/browser/assets/popups/auto-pay/convenience.svg"
        heading="Convenience"
        text="Set it up once and forget about it"
      />
    </ng-template>

    <ng-template #costFree>
      <app-popup-benefit-horizontal
        src="/media/static/app/browser/assets/popups/auto-pay/cost-free.svg"
        heading="Cost-Free"
        text="There are no additional charges for the service"
      />
    </ng-template>

    <ng-template #hassleFree>
      <app-popup-benefit-horizontal
        src="/media/static/app/browser/assets/popups/auto-pay/hassle-free.svg"
        heading="Hassle-Free"
        text="No need to remember due dates"
      />
    </ng-template>

    <ng-template #avoidDelays>
      <app-popup-benefit-horizontal
        src="/media/static/app/browser/assets/popups/auto-pay/avoid-delays.svg"
        heading="Avoid Delays and Late Fees"
        text="Timely payment every month"
      />
    </ng-template>

    <ng-template #flexibility>
      <app-popup-benefit-horizontal
        src="/media/static/app/browser/assets/popups/auto-pay/flexibility.svg"
        heading="Flexibility"
        text="Choose any day between the 1st and the 15th of each month for your Auto Pay draft"
      />
    </ng-template>
  `,
  styles: [
    `
      .autopay-responsive-subtitle {
        display: flex;
        flex-direction: column;
        gap: 10px;
        padding: 10px 10px 10px 10px;

        div {
          display: flex;
          align-items: center;
          justify-content: center;
        }

        @media screen and (min-width: 600px) {
          flex-direction: row;
          padding-bottom: 0px;
        }
      }

      .popup__background {
        background-image: url('/media/static/app/browser/assets/popups/auto-pay/bg-vertical.png');
        background-size: cover;

        @media screen and (min-width: 600px) {
          background-image: url('/media/static/app/browser/assets/popups/auto-pay/bg-horizontal.png');
        }
      }

      .benefits-container {
        &__items {
          display: flex;
          flex-direction: column;
          padding-left: 20px;
          padding-right: 20px;
        }

        &__horizontal-subdivider {
          display: flex;
          flex-direction: row;

          .benefits-container__items {
            flex: 1;
          }
        }
      }
    `,
  ],
})
export class AutopayPopupComponent {
  private dialogRef = inject<MatDialogRef<AutopayPopupComponent>>(MatDialogRef);
  breakpointObserver = inject(BreakpointObserver);

  public isNarrow$ = this.breakpointObserver.observe('(max-width: 599px)').pipe(map((result) => result.matches));

  close(event: MouseEvent) {
    event.preventDefault();
    this.dialogRef.close();
  }
}
